'use client'

import type { ReactNode } from 'react'
import type { ContactTab } from '@/components/blocks/supporter-response'
import type { ReplyChannel, ReplyFeedback } from '@/components/ui/chat'
import type { ChatTagItem } from '@/constants/supporter-response-data'

import { useState } from 'react'
import { useToast } from '@/components/ui/toast'
import { WritingStyleDialog } from '@/components/blocks/writing-style-settings'
import { CHAT_CONVERSATIONS, CHAT_SUPPORTERS, SENT_REPLY_LABEL } from '@/constants/supporter-response-data'

import {
    ComposerFooter,
    MessageBubble,
    ReplyComposer,
    SentNotice,
    ShareIndicators,
    ThreadMessage,
    TimelineEvent
} from '@/components/ui/chat'

import {
    ContactList,
    ContactListToolbar,
    ConversationHeader,
    ConversationPane,
    SupporterProfile,
    SupporterTags
} from '@/components/blocks/supporter-response'

/* Docs views for the Supporter Response blocks (/view/chat-*): each part on its own, with its own state. */

const brandon = CHAT_SUPPORTERS[0]
const brandonToday = CHAT_CONVERSATIONS[0]

function Caption({ children }: { children: ReactNode }) {
    return (
        <p className='text-sm font-medium text-text-secondary'>
            {children}
        </p>
    )
}

export function ChatContactListView() {
    const [tab, setTab] = useState<ContactTab>('high-intent')
    const [selectedId, setSelectedId] = useState(brandonToday.id)
    const [read, setRead] = useState<string[]>([])

    const unread = (id: string) => CHAT_CONVERSATIONS.find(item => item.id === id)?.unread === true && !read.includes(id)

    const items = CHAT_CONVERSATIONS
        .filter(item => tab === 'high-intent' || (tab === 'unread' ? unread(item.id) : item.done))
        .map(item => {
            const supporter = CHAT_SUPPORTERS.find(entry => entry.id === item.supporterId) ?? brandon
            return {
                id: item.id,
                initials: supporter.initials,
                src: supporter.photo,
                summary: `${supporter.name} · ${item.topic}`,
                preview: item.preview,
                time: item.time,
                group: item.group,
                state: item.id === selectedId ? 'selected' as const : item.done ? 'done' as const : unread(item.id) ? 'unread' as const : 'default' as const
            }
        })

    return (
        <ContactList
            className='h-200 max-w-100 overflow-hidden rounded-xl border border-border-default'
            toolbar={(
                <ContactListToolbar
                    tab={tab}
                    onTabChange={setTab}
                    counts={{ 'high-intent': CHAT_CONVERSATIONS.length, 'unread': CHAT_CONVERSATIONS.filter(item => unread(item.id)).length, 'done': CHAT_CONVERSATIONS.filter(item => item.done).length }}
                />
            )}
            items={items}
            groupLabels={{ yesterday: 'Yesterday' }}
            onSelect={id => {
                setSelectedId(id)
                setRead(current => [...current, id])
            }}
            emptyMessage='You are all caught up.'
        />
    )
}

function BrandonThread() {
    return (
        <>
            {brandonToday.thread.map(entry => (
                <ThreadMessage
                    key={entry.message}
                    name={brandon.name}
                    initials={brandon.initials}
                    src={brandon.photo}
                    context={entry.kind === 'mission' ? 'shared their mission story in' : 'shared how they want to stay involved in'}
                    journey={entry.journey}
                >
                    <MessageBubble>
                        {entry.message}
                    </MessageBubble>
                </ThreadMessage>
            ))}
        </>
    )
}

function StatePane({ title, done, messages, reply }: { title: string, done: boolean, messages: ReactNode, reply: ReactNode }) {
    const [isDone, setDone] = useState(done)

    return (
        <div className='flex min-w-0 flex-col gap-3'>
            <Caption>{title}</Caption>
            <ConversationPane
                className='h-220 overflow-hidden rounded-xl border border-border-default'
                header={<ConversationHeader initials={brandon.initials} src={brandon.photo} name={brandon.name} summary='Mission statement · Stay involved' done={isDone} onToggleDone={() => setDone(!isDone)} />}
                messages={(
                    <>
                        <BrandonThread />
                        {messages}
                        {isDone && <TimelineEvent kind='done'>Marked as done</TimelineEvent>}
                    </>
                )}
                reply={reply}
            />
        </div>
    )
}

function ReplyArea({ initial }: { initial: 'messenger' | 'email' | 'sent' }) {
    const { toast } = useToast()
    const [sent, setSent] = useState(initial === 'sent')
    const [channel, setChannel] = useState<ReplyChannel>(initial === 'email' ? 'email' : 'messenger')
    const [message, setMessage] = useState(brandonToday.draft)
    const [subject, setSubject] = useState(brandonToday.subject)
    const [feedback, setFeedback] = useState<ReplyFeedback>(null)
    const [settingsOpen, setSettingsOpen] = useState(false)

    if (sent) return <SentNotice onWriteAnother={() => setSent(false)} />

    return (
        <>
            <TimelineEvent kind='ai-draft'>
                GoodUnited AI drafted a response for Brandon
            </TimelineEvent>
            <ReplyComposer
                channel={channel}
                onChannelChange={setChannel}
                message={message}
                onMessageChange={setMessage}
                subject={subject}
                onSubjectChange={setSubject}
                onSend={() => setSent(true)}
                onCopyEmail={() => {
                    navigator.clipboard?.writeText(`Subject: ${subject}\n\n${message}`).catch(() => undefined)
                    toast({ title: 'Response copied', description: 'Paste it into your email to Brandon.', tone: 'info' })
                }}
                label='Reply to Brandon'
            />
            <ComposerFooter feedback={feedback} onFeedback={setFeedback} onWritingStyle={() => setSettingsOpen(true)} />
            <WritingStyleDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </>
    )
}

export function ChatConversationStatesView() {
    return (
        <div className='grid grid-cols-1 gap-6 xl:grid-cols-3'>
            <StatePane
                title='state=draft'
                done={false}
                messages={<TimelineEvent kind='waiting' name='Brandon' trailing={<ShareIndicators />}>is waiting for a response</TimelineEvent>}
                reply={<ReplyArea initial='messenger' />}
            />
            <StatePane
                title='state=sent'
                done
                messages={(
                    <>
                        <TimelineEvent kind='replied'>{SENT_REPLY_LABEL}</TimelineEvent>
                        <MessageBubble direction='outgoing'>{brandonToday.draft}</MessageBubble>
                    </>
                )}
                reply={<ReplyArea initial='sent' />}
            />
            <StatePane
                title='state=email-marked-sent'
                done
                messages={<TimelineEvent kind='email-replied'>You copied the response and replied via email</TimelineEvent>}
                reply={<ReplyArea initial='email' />}
            />
        </div>
    )
}

export function ChatSupporterProfileView() {
    const [tags, setTags] = useState<ChatTagItem[]>(brandon.tags)
    const [suggested, setSuggested] = useState<ChatTagItem[]>(brandon.suggestedTags)

    return (
        <SupporterProfile
            supporter={brandon}
            className='max-w-85 rounded-xl border border-border-default'
            tags={(
                <SupporterTags
                    tags={tags}
                    suggested={suggested}
                    onAdd={label => setTags(current => current.some(tag => tag.label === label) ? current : [...current, { label, removable: true }])}
                    onRemove={label => setTags(current => current.filter(tag => tag.label !== label))}
                    onAccept={label => {
                        setTags(current => current.some(tag => tag.label === label) ? current : [...current, { label, removable: true }])
                        setSuggested(current => current.filter(tag => tag.label !== label))
                    }}
                    onDismiss={label => setSuggested(current => current.filter(tag => tag.label !== label))}
                />
            )}
        />
    )
}
